import React from 'react';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';

interface RadialProgressProps {
  value: number;
  max: number;
  size?: number;
  thickness?: number;
  color?: string;
  label?: string;
  showValue?: boolean;
  icon?: React.ReactNode;
}

const RadialProgress: React.FC<RadialProgressProps> = ({
  value,
  max,
  size = 120,
  thickness = 10,
  color = '#2563EB',
  label = '',
  showValue = true,
  icon,
}) => {
  const percentage = Math.min((value / max) * 100, 100);
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative inline-flex flex-col items-center group">
      <svg width={size} height={size} className="transform -rotate-90">
        <defs>
          <linearGradient id={`radial-gradient-${label}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity="1" />
            <stop offset="100%" stopColor={color} stopOpacity="0.6" />
          </linearGradient>
          
          <filter id={`radial-glow-${label}`}>
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#E2E8F0"
          strokeWidth={thickness}
        />

        {/* Progress circle */}
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={`url(#radial-gradient-${label})`}
          strokeWidth={thickness}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
          className="group-hover:drop-shadow-lg"
          filter={`url(#radial-glow-${label})`}
        />

        {/* Animated glow on hover */}
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={thickness + 4}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          opacity={0}
          whileHover={{ opacity: 0.2 }}
          transition={{ duration: 0.3 }}
        />
      </svg>

      {/* Center content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {icon && (
          <motion.div
            className="mb-1"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.5, type: 'spring', stiffness: 200 }}
          >
            {icon}
          </motion.div>
        )}
        
        {showValue && (
          <div className="text-xl font-bold" style={{ color }}>
            <CountUp end={value} duration={1.2} separator="," />
          </div>
        )}
        
        {label && (
          <div className="text-xs font-medium text-gray-500 text-center px-2">
            {label}
          </div>
        )}
      </div>

      {/* Percentage badge */}
      <motion.div
        className="absolute -top-2 -right-2 bg-white rounded-full px-2 py-1 shadow-md border border-gray-200"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.8, type: 'spring' }}
      >
        <span className="text-xs font-semibold" style={{ color }}>
          {Math.round(percentage)}%
        </span>
      </motion.div>
    </div>
  );
};

export default RadialProgress;

